import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import api from 'src/lib/api'

const FRAME_W = 1000
const FRAME_H = 600
const SHOWCASE_LOGO_SRC = `${process.env.PUBLIC_URL}/showcase/micromart-logo-black.svg`
/** Balances header grid so tabs stay visually centered. */
const HEADER_LOGO_SLOT_PX = 178

/** NavLink `end` uses strict pathname equality; `/showcase` !== `/showcase/`. */
function normalizePathname(pathname: string): string {
	if (pathname === '/') return pathname
	return pathname.replace(/\/+$/, '')
}

/**
 * Scales the fixed reference design to fill the viewport along each axis independently,
 * so larger displays see larger content on both width and height. Aspect ratio is NOT
 * preserved—kiosk displays are close enough to the 1000×600 reference that the minor
 * stretch is imperceptible, and this avoids letterboxing on non-matching aspect ratios.
 */
function useViewportScale(
	designW: number,
	designH: number
): { x: number; y: number } {
	const [scale, setScale] = useState({ x: 1, y: 1 })

	useLayoutEffect(() => {
		const compute = () => {
			setScale({
				x: window.innerWidth / designW,
				y: window.innerHeight / designH,
			})
		}
		compute()
		window.addEventListener('resize', compute)
		window.addEventListener('orientationchange', compute)
		return () => {
			window.removeEventListener('resize', compute)
			window.removeEventListener('orientationchange', compute)
		}
	}, [designW, designH])

	return scale
}

/**
 * Booth flow: Configure → See it in locations.
 */
const ShowcaseLayout = () => {
	const { pathname } = useLocation()
	const path = normalizePathname(pathname)
	const isSmartStore = path === '/showcase'
	const environmentsTabActive = path === '/showcase/environments'

	const { x: scaleX, y: scaleY } = useViewportScale(FRAME_W, FRAME_H)

	useEffect(() => {
		api.registerDevice()
	}, [])

	useEffect(() => {
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = prev
		}
	}, [])

	return (
		<Viewport>
			<Frame style={{ transform: `scale(${scaleX}, ${scaleY})` }}>
				<HeaderBar>
					<HeaderLogoSlot>
						<HeaderLogo src={SHOWCASE_LOGO_SRC} alt="Micromart" />
					</HeaderLogoSlot>
					<TopBar role="navigation" aria-label="Showcase sections">
						<TabLink
							to="/showcase"
							$active={isSmartStore}
							aria-current={isSmartStore ? 'page' : undefined}
						>
							Configure
						</TabLink>
						<TabLink
							to="/showcase/environments"
							$active={environmentsTabActive}
							aria-current={environmentsTabActive ? 'page' : undefined}
						>
							See it in locations
						</TabLink>
					</TopBar>
					<HeaderBalance aria-hidden />
				</HeaderBar>
				<MainArea>
					<Outlet />
				</MainArea>
			</Frame>
		</Viewport>
	)
}

const Viewport = styled.div`
	position: fixed;
	inset: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	background: radial-gradient(
			120% 80% at 15% 20%,
			rgba(19, 144, 81, 0.18),
			transparent 55%
		),
		linear-gradient(160deg, #f4f7f5 0%, #e8eeeb 45%, #dfe8e4 100%);
	box-sizing: border-box;
	overflow: hidden;
`

/**
 * The design is laid out at a fixed ${FRAME_W}×${FRAME_H} reference; `transform: scale()`
 * is applied inline by `ShowcaseLayout` (per-axis) to fill the viewport on any display.
 */
const Frame = styled.div`
	width: ${FRAME_W}px;
	height: ${FRAME_H}px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	overflow: hidden;
	transform-origin: top left;
	position: absolute;
	top: 0;
	left: 0;
	will-change: transform;
`

const HeaderBar = styled.div`
	flex-shrink: 0;
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 10px 20px 8px;
	box-sizing: border-box;
	gap: 4px;
`

const HeaderLogoSlot = styled.div`
	flex: 0 0 ${HEADER_LOGO_SLOT_PX}px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	min-width: 0;
`

const HeaderLogo = styled.img`
	height: 36px;
	width: auto;
	max-width: ${HEADER_LOGO_SLOT_PX}px;
	object-fit: contain;
	object-position: left center;
	display: block;
`

/** Empty column matching logo width so segment control stays centered in the frame. */
const HeaderBalance = styled.div`
	flex: 0 0 ${HEADER_LOGO_SLOT_PX}px;
	min-width: 0;
`

const TopBar = styled.div`
	flex: 1;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 8px;
	min-width: 0;
`

const TabLink = styled(Link)<{ $active?: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-family: 'GothamMedium', sans-serif;
	font-size: 15px;
	line-height: 1.2;
	padding: 12px 20px;
	border-radius: 999px;
	text-decoration: none;
	color: #2a4a40;
	background: rgba(255, 255, 255, 0.45);
	box-shadow: 0 0 0 1px rgba(8, 55, 46, 0.12);
	-webkit-tap-highlight-color: transparent;
	transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

	${(p) =>
		p.$active &&
		css`
			font-family: 'GothamBold', sans-serif;
			color: #08372e;
			background: #fff;
			box-shadow: 0 4px 20px rgba(8, 55, 46, 0.12),
				0 0 0 2px #139051;
		`}

	&:hover {
		background: rgba(255, 255, 255, 0.65);
		color: #08372e;
		${(p) =>
			p.$active &&
			css`
				background: #fff;
			`}
	}
`

const MainArea = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

export default ShowcaseLayout
